import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, filter, of, switchMap, take } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { DataspaceSelectionService } from '@core/services/dataspace-selection/dataspace-selection.service';
import { DataspaceService } from '@core/services/dataspace/dataspace.service';
import { selectUser } from '@core/store/auth/auth.selectors';
import {
  buildDataspaceUrl,
  extractDataspaceIdFromUrl,
  getRelativePathFromUrl,
  isDataspaceUrl
} from '@core/utils/dataspace-url.utils';

@Injectable({
  providedIn: 'root'
})
export class DataspaceRoutingService {
  private readonly dataspaceService = inject(DataspaceService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly dataspaceSelection = inject(DataspaceSelectionService);

  /**
   * Get the full URL for a relative path within the current dataspace.
   *
   * @param {string} relativePath - Relative path to append to the current dataspace URL.
   * @returns {string} - Full URL for the relative path within the current dataspace.
   */
  public getFullUrlForRelativePath(relativePath: string): string {
    const currentDataspaceId = extractDataspaceIdFromUrl(this.router.url);
    if (currentDataspaceId) {
      return buildDataspaceUrl(currentDataspaceId, decodeURIComponent(relativePath));
    } else {
      console.error('No dataspace ID found in the current URL.');
      return '';
    }
  }

  /**
   * Get the default dataspace ID of the current user.
   *
   * @returns {Observable<number | null>}
   */
  public getUserDefaultDataspaceId$(): Observable<number | null> {
    return this.store.select(selectUser).pipe(
      filter((user) => !!user),
      map((user) => user?.defaultDataSpace.id ?? null),
      distinctUntilChanged()
    );
  }

  /**
   * Check if the given URL contains a dataspace identifier.
   *
   * @param {string} url - URL to check for a dataspace identifier.
   * @returns {boolean}
   */
  public isUrlContainDataspace(url: string): boolean {
    return isDataspaceUrl(url);
  }

  /**
   * Navigate to a dataspace with a specific relative path.
   *
   * @param {number} dataspaceId - ID of the dataspace to navigate to.
   * @param {string | undefined} relativePath - Relative path to append to the dataspace
   *   URL.
   * @returns {Promise<boolean>} - Promise that resolves to true if navigation is
   *   successful, false otherwise.
   */
  public navigateToDataspace(dataspaceId: number, relativePath?: string): Promise<boolean> {
    const url = buildDataspaceUrl(dataspaceId, relativePath);
    return this.router.navigateByUrl(url);
  }

  /**
   * Navigate to the default dataspace of the user, or to the first available
   *
   * @param {string | undefined} relativePath - Relative path to append to the default
   *   dataspace URL.
   * @returns {Observable<boolean>}
   */
  public navigateToDefaultDataspace(relativePath?: string): Observable<boolean> {
    return this.getUserDefaultDataspaceId$().pipe(
      take(1),
      switchMap((defaultDataspaceId) => {
        if (defaultDataspaceId) {
          return this.navigateToDataspace(defaultDataspaceId, relativePath);
        } else {
          console.warn("Aucun espace de travail par défaut défini pour l'utilisateur");
          return this.navigateToFirstAvailableDataspace(relativePath);
        }
      })
    );
  }

  /**
   * Navigate to the first available dataspace
   *
   * @param {string | undefined} relativePath - Relative path to append to the dataspace
   *   URL.
   * @returns {Observable<boolean>} - Observable that emits true if navigation is
   *   successful, false otherwise.
   */
  public navigateToFirstAvailableDataspace(relativePath?: string): Observable<boolean> {
    return this.dataspaceService.getAllDataSpaces$().pipe(
      switchMap((dataspaces) => {
        if (dataspaces && dataspaces.length > 0) {
          const firstDataspace = dataspaces[0];
          console.log(
            `Redirection vers le premier espace disponible: ${firstDataspace.id}`
          );
          return this.navigateToDataspace(firstDataspace.id, relativePath);
        } else {
          console.error('Aucun espace de travail disponible');
          this.router.navigate(['/no-dataspace']);
          return of(false);
        }
      })
    );
  }

  /**
   * Navigate to a specific page within the current dataspace.
   *
   * @param {string} relativePath - Relative path to the page within the current dataspace.
   * @returns {Promise<boolean>}
   */
  public navigateToPageInDataspace(relativePath: string): Promise<boolean> {
    const dataspaceIdStr = extractDataspaceIdFromUrl(this.router.url);
    if (dataspaceIdStr) {
      return this.navigateToDataspace(parseInt(dataspaceIdStr, 10), relativePath);
    } else {
      console.error('No dataspace ID found in the current URL.');
      return Promise.resolve(false);
    }
  }

  /**
   * Navigate to a page within the current dataspace if available, otherwise to the last
   * selected dataspace, otherwise to the default dataspace.
   *
   * @param {string} relativePath - Relative path to the page.
   * @returns {Promise<boolean>} - Promise that resolves to true if navigation is successful,
   *   false otherwise.
   */
  public navigateToPageInDataspaceOrFallbackToDefault(
    relativePath: string
  ): Promise<boolean> {
    if (isDataspaceUrl(this.router.url)) {
      return this.navigateToPageInDataspace(relativePath);
    }

    const selectedId = this.dataspaceSelection.selectedId();
    return selectedId != null
      ? this.navigateToDataspace(selectedId, relativePath)
      : (this.navigateToDefaultDataspace(relativePath)
          .toPromise()
          ?.then((v) => v ?? false) ?? Promise.resolve(false));
  }

  /**
   * Switch to a dataspace by its ID, preserving the current relative path.
   *
   * @param {number} dataspaceId - ID of the dataspace to switch to.
   * @returns {Promise<boolean>} - Promise that resolves to true if the navigation succeeds,
   *   false otherwise.
   */
  public switchToDataspace(dataspaceId: number): Promise<boolean> {
    const currentPath = getRelativePathFromUrl(this.router.url);
    return this.navigateToDataspace(dataspaceId, currentPath);
  }
}
